import type { BlacklistAutoRule } from '@hprofits/api';
import { compareAdTypeDisplayOrder } from '@hprofits/shared';

import type { RuleGroupRow } from '../types/ruleGroupRow';

const toActionLabel = (item: BlacklistAutoRule, actionLabels: ReadonlyMap<number, string>): string =>
  item.action.state !== null ? (actionLabels.get(item.action.state) ?? String(item.action.state)) : '';

const stringifyCondition = (item: BlacklistAutoRule): string =>
  item.conditions.map((condition) => `${condition.param} ${condition.operator} ${condition.value}`).join(' AND ');

// Any type set marks a group, even a single rule's: the type IS the group sign, so a lone typed
// rule forms its own one-member group. Only typeless rules stay standalone ("without a group").
export const MIN_RULES_TO_GROUP = 1;

const GROUP_ID_PREFIX = 'adtype-';

// A group's identity IS its ad-unit-type set, order-independent: same set, same group; a single
// type and a combination are keyed alike. `null` means no type at all, which is never grouped.
// Exported because a screen that rewrites rules' types has to work out which group they land in.
export const toGroupId = (typeIds: ReadonlyArray<number>): string | null =>
  typeIds.length > 0 ? `${GROUP_ID_PREFIX}${[...typeIds].sort((a, b) => a - b).join('-')}` : null;

export const fromGroupId = (groupId: string): ReadonlyArray<number> =>
  groupId.startsWith(GROUP_ID_PREFIX)
    ? groupId
        .slice(GROUP_ID_PREFIX.length)
        .split('-')
        .map(Number)
        .filter((id) => Number.isInteger(id))
    : [];

const toTypeKey = (item: BlacklistAutoRule): string | null => toGroupId(item.adUnitTypes.map((type) => type.id));

type TypeRef = Readonly<{ id: number; name: string }>;

// Every place that spells a type set out goes through here, so a group reads the same in the list,
// its breadcrumb, its page title and the merge warning.
export const toTypeSetLabel = (types: ReadonlyArray<TypeRef>): string =>
  [...types]
    .sort((a, b) => compareAdTypeDisplayOrder(a.name, b.name) || a.id - b.id)
    .map((type) => type.name)
    .join(', ');

const toTypeLabel = (item: BlacklistAutoRule): string => toTypeSetLabel(item.adUnitTypes);

type RuleAdUnitRef = BlacklistAutoRule['adUnits'][number];

const toAdUnitItems = (units: ReadonlyArray<RuleAdUnitRef>): RuleGroupRow['adUnitItems'] =>
  units.map((unit) => ({ id: unit.id, name: unit.name, typeId: unit.adTypeId }));

// A typed rule's list is its exclusions — what the group page seeds and Apply writes — and its
// includes ride along apart, so neither hides the other.
const toOwnAdUnits = (
  item: BlacklistAutoRule,
): Pick<RuleGroupRow, 'adunitIds' | 'adUnitItems' | 'exclude' | 'includedAdunitIds' | 'includedAdUnitItems'> => {
  if (item.adUnitTypes.length > 0) {
    return {
      adunitIds: item.excludeAdUnits.map((unit) => String(unit.id)),
      adUnitItems: toAdUnitItems(item.excludeAdUnits),
      exclude: true,
      includedAdunitIds: item.adUnits.map((unit) => String(unit.id)),
      includedAdUnitItems: toAdUnitItems(item.adUnits),
    };
  }
  if (item.adUnits.length > 0) {
    return {
      adunitIds: item.adUnits.map((unit) => String(unit.id)),
      adUnitItems: item.adUnits.map((unit) => ({ id: unit.id, name: unit.name, typeId: unit.adTypeId })),
      exclude: false,
    };
  }
  if (item.excludeAdUnits.length > 0) {
    return {
      adunitIds: item.excludeAdUnits.map((unit) => String(unit.id)),
      adUnitItems: item.excludeAdUnits.map((unit) => ({ id: unit.id, name: unit.name, typeId: unit.adTypeId })),
      exclude: true,
    };
  }
  return { adunitIds: [], adUnitItems: [], exclude: false };
};

const toGroupRow = (groupId: string, label: string): RuleGroupRow => ({
  id: groupId,
  parentId: null,
  kind: 'group',
  name: label,
  adUnitType: label,
  adunitIds: [],
  adUnitItems: [],
  condition: '',
  action: '',
});

const toRuleRow = (
  item: BlacklistAutoRule,
  groupId: string | null,
  actionLabels: ReadonlyMap<number, string>,
): RuleGroupRow => ({
  id: `rule-${item.id}`,
  parentId: groupId,
  sourceRuleId: item.id,
  kind: 'rule',
  name: item.name,
  adUnitType: groupId ? '' : toTypeLabel(item),
  ...toOwnAdUnits(item),
  condition: stringifyCondition(item),
  action: toActionLabel(item, actionLabels),
});

export const toRuleGroupRows = (
  items: ReadonlyArray<BlacklistAutoRule>,
  // Resolves each rule's `action.state` id to its dictionary label. Optional: a caller that only
  // needs the grouping (the editor's parent-group lookup) has no reason to fetch that dictionary.
  actionLabels: ReadonlyMap<number, string> = new Map(),
): ReadonlyArray<RuleGroupRow> => {
  const groupIds = new Set<string>();
  const rows: Array<RuleGroupRow> = [];
  // A group's exclusions are the union of its rules' — the same list the group page seeds and Apply
  // writes. Keyed by unit id so the popover details survive the dedupe.
  const groupExclusions = new Map<string, Map<number, { name: string; typeId: number }>>();

  // Rows keep the items' order, so the list's sort holds across groups and lone rules: a group
  // takes the place of its first rule — its newest one under the default id-descending sort.
  for (const item of items) {
    // Any type set is a group (see MIN_RULES_TO_GROUP); only typeless rules stay standalone.
    const groupId = toTypeKey(item);
    if (groupId && !groupIds.has(groupId)) {
      groupIds.add(groupId);
      rows.push(toGroupRow(groupId, toTypeLabel(item)));
    }
    rows.push(toRuleRow(item, groupId, actionLabels));
    if (groupId) {
      const exclusions = groupExclusions.get(groupId) ?? new Map<number, { name: string; typeId: number }>();
      item.excludeAdUnits.forEach((unit) => exclusions.set(unit.id, { name: unit.name, typeId: unit.adTypeId }));
      groupExclusions.set(groupId, exclusions);
    }
  }

  return rows.map((row) => {
    const exclusions = row.kind === 'group' ? groupExclusions.get(row.id) : undefined;
    if (!exclusions) {
      return row;
    }
    const sorted = [...exclusions.entries()].sort((a, b) => a[0] - b[0]);
    return {
      ...row,
      adunitIds: sorted.map(([id]) => String(id)),
      adUnitItems: sorted.map(([id, { name, typeId }]) => ({ id, name, typeId })),
      exclude: true,
    };
  });
};
